import { Spacing } from '@persona-id/design-tokens';
import { FC } from 'react';
import styled from 'styled-components';

import useOnEnhancedLearnMoreClick from 'dashboard/apps/InquiryTemplateEditor/hooks/useOnEnhancedLearnMoreClick';
import LearnMoreTooltip from 'dashboard/apps/InquiryTemplateEditor/LearnMoreTooltip';
import StepConfigTypeCard from 'dashboard/components/WorkflowControlPanel/StepConfigTypeCard';
import { DEFAULT_WAIT_PARAMS } from 'dashboard/components/WorkflowControlPanel/StepConfigWait';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { useCopyWorkflowSteps } from 'dashboard/components/WorkflowEditor/WorkflowCanvas';
import { EMPTY_LOGIC_BLOCK_OR_V2 } from 'dashboard/lib/workflows';
import { Feature } from 'dashboard/models/inquiry-template-version';
import { WorkflowError } from 'dashboard/models/workflow';
import {
  ActualWorkflowStepType,
  newPlaceholderStep,
  NullWorkflowAction,
  NullWorkflowStepActionKey,
  WORKFLOW_STEP_NEW_ACTION_NAME,
  WorkflowStep,
  WorkflowStepAction,
  WorkflowStepConditional,
  WorkflowStepLoop,
  WorkflowStepLoopModeType,
  WorkflowStepLoopType,
  WorkflowStepModule,
  WorkflowStepParallel,
  WorkflowStepPlaceholder,
  WorkflowStepType,
  WorkflowStepWait,
} from 'dashboard/models/workflow-version';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { assertNever } from 'lib/utils/types';
import { generateWorkflowToken } from 'lib/workflows';

export const INQUIRY_WORKFLOW_PERMITTED_TYPES: Partial<WorkflowStepType>[] = [
  WorkflowStepType.Action,
  WorkflowStepType.Parallel,
  WorkflowStepType.Wait,
];

const ALL_STEP_TYPES: ActualWorkflowStepType[] = [
  WorkflowStepType.Action,
  WorkflowStepType.Conditional,
  WorkflowStepType.Loop,
  WorkflowStepType.Module,
  WorkflowStepType.Parallel,
  WorkflowStepType.Wait,
];

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const StepTypeCardContainer = styled.div`
  margin-bottom: ${Spacing[4]};
`;

interface StepConfigNewProps {
  activeStep: WorkflowStepPlaceholder;
  editable: boolean;
  errors: WorkflowError[];
  showFullWorkflowTree?: boolean;
  isInquiryWorkflow: boolean;
  deleteStep: (stepToDelete: WorkflowStep) => void;
  updateStep: (newStep: WorkflowStep) => void;
  pasteCopiedSteps: (currStep: WorkflowStep) => void;
  disabledStepTypes: WorkflowStepType[];
  stepTypeTooltipTexts: Partial<Record<WorkflowStepType, string>>;
  disallowedStepTypes?: ActualWorkflowStepType[];
}

const StepConfigNew: FC<StepConfigNewProps> = ({
  activeStep,
  editable,
  errors,
  showFullWorkflowTree,
  isInquiryWorkflow,
  deleteStep,
  updateStep,
  pasteCopiedSteps,
  disabledStepTypes,
  disallowedStepTypes = [],
  stepTypeTooltipTexts,
}) => {
  const handleEnhancedLearnMoreClick = useOnEnhancedLearnMoreClick();
  const stepTypes = ALL_STEP_TYPES.filter((stepType) => !disallowedStepTypes.includes(stepType));
  const [copiedStepsData] = useCopyWorkflowSteps();

  const enableFleWorkflowLoopStep = useFeatureFlag('dashboard:fle-enable-workflow-loop-step');

  const selectStepType = (stepType: ActualWorkflowStepType) => {
    switch (stepType) {
      case WorkflowStepType.Action: {
        const newStep: WorkflowStepAction = {
          ...activeStep,
          name: WORKFLOW_STEP_NEW_ACTION_NAME[WorkflowStepType.Action],
          type: stepType,
          action: {
            key: NullWorkflowStepActionKey.NullKey,
          } as NullWorkflowAction,
        };

        updateStep(newStep);
        break;
      }
      case WorkflowStepType.Wait: {
        const newStep: WorkflowStepWait = {
          ...activeStep,
          name: WORKFLOW_STEP_NEW_ACTION_NAME[WorkflowStepType.Wait],
          type: stepType,
          ...DEFAULT_WAIT_PARAMS,
        };

        updateStep(newStep);
        break;
      }
      case WorkflowStepType.Conditional: {
        const newStep: WorkflowStepConditional = {
          ...activeStep,
          name: WORKFLOW_STEP_NEW_ACTION_NAME[WorkflowStepType.Conditional],
          type: stepType,
          branches: [[newPlaceholderStep()], []],
          conditionals: [
            {
              token: generateWorkflowToken(),
              name: '',
              rules: EMPTY_LOGIC_BLOCK_OR_V2,
            },
            {
              token: generateWorkflowToken(),
              name: 'Else',
              rules: true,
            },
          ],
        };

        updateStep(newStep);
        break;
      }
      case WorkflowStepType.Parallel: {
        const newStep: WorkflowStepParallel = {
          ...activeStep,
          name: WORKFLOW_STEP_NEW_ACTION_NAME[WorkflowStepType.Parallel],
          type: stepType,
          branches: [[newPlaceholderStep()], [newPlaceholderStep()]],
        };
        updateStep(newStep);
        break;
      }
      case WorkflowStepType.Loop: {
        const newStep: WorkflowStepLoop = {
          ...activeStep,
          name: WORKFLOW_STEP_NEW_ACTION_NAME[WorkflowStepType.Loop],
          type: stepType,
          branch: [newPlaceholderStep()],
          settings: {
            type: WorkflowStepLoopType.ForEach,
            'array-path': '',
            mode: WorkflowStepLoopModeType.SEQUENTIAL,
          },
        };
        updateStep(newStep);
        break;
      }
      case WorkflowStepType.Module: {
        const newStep: WorkflowStepModule = {
          ...activeStep,
          name: WORKFLOW_STEP_NEW_ACTION_NAME[WorkflowStepType.Module],
          type: stepType,
          'input-mappings': [],
          'child-workflow-block-id': '',
        };
        updateStep(newStep);
        break;
      }
      default:
        assertNever(stepType);
    }
  };

  const permittedStepTypes = isInquiryWorkflow
    ? stepTypes.filter((type) => {
        if (showFullWorkflowTree) {
          return [
            ...INQUIRY_WORKFLOW_PERMITTED_TYPES,
            WorkflowStepType.Conditional,
            ...(enableFleWorkflowLoopStep ? [WorkflowStepType.Loop] : []),
          ].includes(type);
        } else {
          return INQUIRY_WORKFLOW_PERMITTED_TYPES.includes(type);
        }
      })
    : stepTypes;

  const cards = permittedStepTypes.map((type) => {
    const disabled = disabledStepTypes.includes(type);
    const tooltipText = stepTypeTooltipTexts[type];
    return (
      <StepTypeCardContainer key={type}>
        <LearnMoreTooltip
          id={`workflow-step-config-tooltip__${type}`}
          tooltipContent={tooltipText}
          onClick={() => handleEnhancedLearnMoreClick({ feature: Feature.Asset })}
          side='top'
          disabled={tooltipText == null}
          trigger={
            <div data-test={`workflow-new-step__${type}`}>
              <StepConfigTypeCard
                type={type}
                disabled={disabled}
                onClickStepType={() => selectStepType(type)}
              />
            </div>
          }
        />
      </StepTypeCardContainer>
    );
  });

  const { renderErrorMessageFor } = useWorkflowError<keyof WorkflowStepPlaceholder>(errors);

  return (
    <Container>
      <Heading size='sm'>Add Step</Heading>
      <Paragraph>Select from the list below to add a new Step.</Paragraph>
      <Spacer height={4} />
      <Heading size='sm'>Step Types</Heading>
      <Spacer height={2} />
      {cards}
      {renderErrorMessageFor('type')}
      <Spacer height={2} />
      {(copiedStepsData?.count ?? 0) > 0 && (
        <>
          <Button
            id='workflow-step-control-panel__paste-steps-button'
            type='button'
            variant='basic'
            onClick={() => pasteCopiedSteps(activeStep)}
          >
            {copiedStepsData.count > 1 ? `Paste ${copiedStepsData.count} steps` : `Paste a step`}
          </Button>
          <Spacer height={2} />
        </>
      )}
      {editable && (
        <Button
          type='button'
          id='workflow-step-control-panel__button_delete'
          onClick={() => deleteStep(activeStep)}
        >
          Cancel
        </Button>
      )}
    </Container>
  );
};

export default StepConfigNew;
